/*
 *  -----------------------------------------------------------------------
 *    <copyright file="import-study/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import { useTranslation } from 'hooks';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import './styles.scss';
import { MODAL_CLOSE_ACTIONS } from '@utils/constants';
import NotificationService from '@utils/notification';
import { GET_MANAGE_ORG_STUDIES, UPDATE_STUDY_READONLY_FIELD } from '@queries/studies';

const EditStudyReadOnlyDialog = ({
    studyId,
    onClose,
    studyReadOnly,
    recordId,
}: {
    studyId: string;
    onClose: Function;
    studyReadOnly: boolean;
    recordId: string;
}) => {
    const { t } = useTranslation();

    const [updateStudyReadOnlyField] = useMutation(UPDATE_STUDY_READONLY_FIELD);

    const submit = () => {
        updateStudyReadOnlyField({
            variables: { studyId, isReadOnly: studyReadOnly },
            refetchQueries: [{ query: GET_MANAGE_ORG_STUDIES }],
        })
            .then(() => {
                NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: recordId }));
                onClose();
            })
            .catch(() => onClose(MODAL_CLOSE_ACTIONS.ERROR));
    };

    const dialogTitle = studyReadOnly
        ? t('admin:studyReadOnly.dialog.enable.title')
        : t('admin:studyReadOnly.dialog.disable.title');

    const dialogContent = studyReadOnly
        ? t('admin:studyReadOnly.dialog.enable.message')
        : t('admin:studyReadOnly.dialog.disable.message');

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            maxWidth="md"
            fullWidth
            scroll="paper"
            open
        >
            <DialogTitle id="set-study-read-only-dialog">
                {dialogTitle}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="study-read-only-dialog-content">
                <div className="info-box-wrapper">
                    <DialogInformationBox topText={dialogContent} />
                </div>

                <div className="message">{t('common:wouldLikeContinue')}</div>
                <DialogActions>
                    <Button id="update-study-read-only-btn" text={t('common:confirm')} onClick={submit} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditStudyReadOnlyDialog;
